import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

/**
 * Intercepts browser back/forward navigation shortcuts while a doorsale view
 * is mounted and calls `onBack` / `onForward` instead, keeping navigation in
 * sync with the doorsale step-order buttons rather than the browser history stack.
 *
 * Intercepted back events:
 *   - mousedown button 3       (side "back" button on 5-button mice)
 *   - keydown Backspace        (when not inside an input / textarea / contenteditable)
 *   - keydown Alt+ArrowLeft  / Meta+ArrowLeft  (Windows / macOS back shortcut)
 *
 * Intercepted forward events (only when onForward is provided):
 *   - mousedown button 4       (side "forward" button on 5-button mice)
 *   - keydown Alt+ArrowRight / Meta+ArrowRight
 */
export function useDoorsaleGestureNav(onBack?: () => void, onForward?: () => void) {
	const router = useRouter();

	function isEditableTarget(target: EventTarget | null): boolean {
		if (!(target instanceof Element)) return false;
		const tag = target.tagName;
		if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
		if ((target as HTMLElement).isContentEditable) return true;
		return false;
	}

	/**
	 * Registers a one-shot beforeEach guard that blocks the next navigation
	 * targeting `fromPath`. Used to swallow the browser's own popstate after
	 * we've already called our programmatic navigation, since preventDefault()
	 * on mousedown is not fully reliable for back/forward side-buttons.
	 */
	function blockPopstateTo(fromPath: string) {
		const unguard = router.beforeEach((to) => {
			if (to.fullPath === fromPath) {
				unguard();
				return false;
			}
			return true;
		});
		setTimeout(unguard, 500);
	}

	function handleMouseDown(e: MouseEvent) {
		if (e.button === 3 && onBack) {
			e.preventDefault();
			const fromPath = router.currentRoute.value.fullPath;
			onBack();
			blockPopstateTo(fromPath);
		} else if (e.button === 4 && onForward) {
			e.preventDefault();
			const fromPath = router.currentRoute.value.fullPath;
			onForward();
			blockPopstateTo(fromPath);
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'Backspace' && !isEditableTarget(e.target) && onBack) {
			e.preventDefault();
			onBack();
			return;
		}
		if (e.key === 'ArrowLeft' && (e.altKey || e.metaKey) && onBack) {
			e.preventDefault();
			onBack();
			return;
		}
		if (e.key === 'ArrowRight' && (e.altKey || e.metaKey) && onForward) {
			e.preventDefault();
			onForward();
		}
	}

	onMounted(() => {
		if (!onBack && !onForward) return;
		window.addEventListener('mousedown', handleMouseDown);
		window.addEventListener('keydown', handleKeyDown);
	});

	onUnmounted(() => {
		if (!onBack && !onForward) return;
		window.removeEventListener('mousedown', handleMouseDown);
		window.removeEventListener('keydown', handleKeyDown);
	});
}

